import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

import bunny1 from "../assets/bunny1.png";
import bunny2 from "../assets/bunny2.png";
import bunny3 from "../assets/bunny3.png";
import bunny4 from "../assets/bunny4.png";

const clients = [
  {
    id: 0,
    title: "Startups",
    desc: "Got a big idea but your brand still looks like a draft? We help startups turn rough ideas into clean, confident brands that actually get noticed.",
    img: bunny2,
    bgColor: "rgba(230,180,180,0.3)",
  },
  {
    id: 1,
    title: "Small & Growing Businesses",
    desc: "Doing great work but your visuals aren't showing it? We upgrade your branding and digital presence so customers stop scrolling... and start noticing.",
    img: bunny3,
    bgColor: "rgba(180,200,230,0.3)",
  },
  {
    id: 2,
    title: "Brands Ready to Scale",
    desc: "Already running but stuck at the same level? We help you level up with better design, stronger presence, and marketing that actually performs.",
    img: bunny1,
    bgColor: "rgba(230,210,180,0.3)",
  },
  {
    id: 3,
    title: "Founders & Entrepreneurs",
    desc: "You build the business, handle the chaos, and chase growth — we make sure your brand looks as sharp as your vision.",
    img: bunny4,
    bgColor: "rgba(210,185,230,0.3)",
  },
];

const total = clients.length;

/* ── Desktop slot layout (unchanged) ── */
const getSlotStyle = (index, current) => {
  const diff = ((index - current) % total + total) % total;
  const map = {
    0: { x: 0,       scale: 1.5,  zIndex: 10, opacity: 1, size: 450 },
    1: { x: "38vw",  scale: 0.75, zIndex: 5,  opacity: 1, size: 450 },
    [total - 1]: { x: "-38vw", scale: 0.75, zIndex: 5, opacity: 1, size: 450 },
    2: { x: "68vw",  scale: 0.55, zIndex: 2,  opacity: 1, size: 450 },
    [total - 2]: { x: "-68vw", scale: 0.55, zIndex: 2, opacity: 1, size: 450 },
  };
  return (
    map[diff] || {
      x: diff < total / 2 ? "100vw" : "-100vw",
      scale: 0.4,
      zIndex: 1,
      opacity: 0,
      size: 100,
    }
  );
};

/* ── Mobile slot layout ── */
const getMobileSlotStyle = (index, current) => {
  const diff = ((index - current) % total + total) % total;
  // centre=0, right=1, left=total-1, hidden otherwise
  const map = {
    0:         { x: 0,       scale: 1,    zIndex: 10, opacity: 1 },
    1:         { x: "75vw",  scale: 0.65, zIndex: 5,  opacity: 0.7 },
    [total-1]: { x: "-75vw", scale: 0.65, zIndex: 5,  opacity: 0.7 },
  };
  return (
    map[diff] || {
      x: diff < total / 2 ? "150vw" : "-150vw",
      scale: 0.4,
      zIndex: 1,
      opacity: 0,
    }
  );
};

const WhoWeWorkWith = () => {
  const [current, setCurrent] = useState(1);

  const prev = () => setCurrent((c) => (c - 1 + total) % total);
  const next = () => setCurrent((c) => (c + 1) % total);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrent((c) => (c + 1) % total);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  return (
    <section
      className="relative bg-[#F5F5F5] py-16 px-4 overflow-hidden"
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      {/* ── Header ── */}
      <div className="text-center mb-12">
        <p
          className="text-xl font-semibold tracking-[0] uppercase mb-3"
          style={{
            display: "inline-block",
            background: "linear-gradient(135deg, #129ADC, #B55DD0, #E154AA, #F45A39)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          Who We Work With
        </p>
        <h2
          className="text-3xl md:text-4xl font-bold text-gray-900"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        >
          For brands done with boring design
        </h2>
      </div>

      {/* ══════════════════════════════════
          DESKTOP CAROUSEL  (md and up)
      ══════════════════════════════════ */}
      <div className="hidden md:block">
        {/* Stage */}
        <div className="relative h-[700px] w-full flex items-center justify-center overflow-hidden">
          {clients.map((client, i) => {
            const { x, scale, zIndex, opacity, size } = getSlotStyle(i, current);
            return (
              <motion.div
                key={client.id}
                animate={{ x, scale, opacity, zIndex }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                className="absolute flex flex-col items-center"
                style={{ width: size, cursor: i !== current ? "pointer" : "default" }}
                onClick={() => i !== current && setCurrent(i)}
              >
                <div
                  className="relative flex items-center justify-center"
                  style={{ width: size, height: size }}
                >
                  <img
                    src={client.img}
                    alt={client.title}
                    className="relative z-10 object-contain"
                    style={{ width: size * 5, height: size * 2.78 }}
                  />
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Bottom row — text left, arrows right */}
        <div className="relative w-full flex items-end justify-between px-4 -mt-24">
          <div className="w-[300px] ml-[calc(50%-38vw-85px)]">
            <AnimatePresence mode="wait">
              <motion.div
                key={current}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.35 }}
                className="text-left"
              >
                <h4
                  className="text-xs font-bold uppercase tracking-widest text-black mb-2"
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                >
                  {clients[current].title}
                </h4>
                <p className="text-xs text-black leading-relaxed text-justify">
                  {clients[current].desc}
                </p>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Desktop arrows */}
          <div className="relative z-50 flex gap-3 items-center">
            <button
              onClick={prev}
              className="w-9 h-9 rounded-full border border-gray-300 bg-white flex items-center justify-center text-gray-500 hover:border-[#129ADC] hover:text-[#129ADC] transition-colors duration-200"
              type="button"
            >
              ←
            </button>
                  
            <button
              onClick={next}
              className="w-9 h-9 rounded-full border border-gray-300 bg-white flex items-center justify-center text-gray-500 hover:border-[#129ADC] hover:text-[#129ADC] transition-colors duration-200"
              type="button"
            >
              →
            </button>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════
          MOBILE CAROUSEL  (below md)
      ══════════════════════════════════ */}
      <div className="md:hidden">
        {/* Stage — smaller height, only centre + peek cards */}
        <div className="relative h-[320px] w-full flex items-center justify-center overflow-hidden">
          {clients.map((client, i) => {
            const { x, scale, zIndex, opacity } = getMobileSlotStyle(i, current);
            const SIZE = 220; // px — base card size on mobile
            return (
              <motion.div
                key={client.id}
                animate={{ x, scale, opacity, zIndex }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                className="absolute flex flex-col items-center"
                style={{
                  width: SIZE,
                  cursor: i !== current ? "pointer" : "default",
                }}
                onClick={() => i !== current && setCurrent(i)}
              >
                <img
                  src={client.img}
                  alt={client.title}
                  className="object-contain w-full"
                  style={{ height: SIZE * 1.4 }}
                />
              </motion.div>
            );
          })}
        </div>

        {/* Text block — centred below the image */}
        <div className="mt-4 px-6 text-center">
          <AnimatePresence mode="wait">
            <motion.div
              key={current}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.35 }}
            >
              <h4
                className="text-xs font-bold uppercase tracking-widest text-black mb-2"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                {clients[current].title}
              </h4>
              <p className="text-xs text-black leading-relaxed">
                {clients[current].desc}
              </p>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Arrows + dot indicators */}
        <div className="flex flex-col items-center gap-3 mt-6">
          <div className="flex gap-3">
            <button
              onClick={prev}
              className="w-9 h-9 rounded-full border border-gray-300 bg-white flex items-center justify-center text-gray-500 hover:border-[#129ADC] hover:text-[#129ADC] transition-colors duration-200"
            >
              ←
            </button>
            <button
              onClick={next}
              className="w-9 h-9 rounded-full border border-gray-300 bg-white flex items-center justify-center text-gray-500 hover:border-[#129ADC] hover:text-[#129ADC] transition-colors duration-200"
            >
              →
            </button>
          </div>

          {/* Dot indicators */}
          <div className="flex gap-2">
            {clients.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                className="transition-all duration-200"
                style={{
                  width: i === current ? 20 : 8,
                  height: 8,
                  borderRadius: 99,
                  background:
                    i === current
                      ? "linear-gradient(135deg, #129ADC, #B55DD0)"
                      : "#D1D5DB",
                  border: "none",
                  cursor: "pointer",
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhoWeWorkWith;